import React from 'react';
import { createTheme } from '@material-ui/core/styles';
import { colorPalette } from './colorPalette';
import { ChevronDownOutlined, Close } from '@next-common/material-ui-icons';
import { IconButton } from '@material-ui/core';
import { RadioButtonIcon } from './components/RadioButtonIcon';
export const globalTheme = (color, htmlFontSize = 10, _pxToRem) => {
    const colorArray = {
        main: colorPalette.red800,
        secondary: colorPalette.red700,
        third: colorPalette.red900,
        text: colorPalette.white,
        outline: null, // Outline variation (optional)
    };
    if (color === 'green') {
        colorArray.main = colorPalette.green700;
        colorArray.secondary = colorPalette.green600;
        colorArray.third = colorPalette.green800;
        colorArray.text = colorPalette.white;
    }
    else if (color === 'tertiary') {
        colorArray.main = colorPalette.gray200;
        colorArray.secondary = colorPalette.gray300;
        colorArray.third = colorPalette.gray100;
        colorArray.text = colorPalette.gray1000;
        colorArray.outline = colorPalette.gray600;
    }
    const fontSize = 14; // px
    // Tell Material-UI what's the font-size on the html element.
    // 16px is the default font-size used by browsers.
    const coef = fontSize / 14;
    let pxToRem = (size) => `${(size / htmlFontSize) * coef}rem`;
    if (_pxToRem) {
        pxToRem = _pxToRem;
    }
    const theme = {
        typography: {
            pxToRem,
            htmlFontSize,
            fontFamily: ['RakutenSansUi', 'RakutenSansUiBlack', 'RakutenSansUiLight', 'RakutenSansUiSemiBold', 'sans-serif'].join(','),
            fontWeightMedium: 600,
            h1: {
                fontSize: pxToRem(24),
                fontWeight: 400,
                lineHeight: 1.208,
            },
            h2: {
                fontSize: pxToRem(20),
                fontWeight: 400,
                lineHeight: 1.2,
            },
            h3: {
                fontSize: pxToRem(18),
                fontWeight: 700,
                lineHeight: 1.056,
            },
            body1: {
                fontSize: pxToRem(14),
                lineHeight: 1.214,
            },
            body2: {
                fontSize: pxToRem(14),
                lineHeight: 1.214,
            },
            button: {
                textTransform: 'none',
            },
        },
        shadows: ['none'],
        breakpoints: {
            values: {
                // breakpoint naming used from material-ui
                xs: 0,
                sm: 576,
                ta: 768,
                md: 992,
                lg: 1200,
                ew: 1366,
                xl: 1920, // material-ui modified 1920
            },
        },
        palette: {
            common: {
                black: colorPalette.gray1000,
                white: colorPalette.white,
            },
            primary: {
                main: colorArray.main,
                dark: colorArray.secondary,
                contrastText: colorPalette.white,
            },
            secondary: {
                main: colorPalette.gray1000,
                dark: colorPalette.gray900,
                contrastText: colorPalette.white,
            },
            error: {
                main: colorPalette.red600,
            },
            info: {
                main: colorPalette.lightBlue800,
            },
            success: {
                main: colorPalette.green800,
            },
            warning: {
                main: colorPalette.orange600,
            },
            background: {
                default: colorPalette.white,
            },
            action: {
                disabled: colorPalette.white,
                disabledBackground: colorPalette.gray700,
            },
            text: {
                primary: colorPalette.gray1000,
                secondary: colorPalette.gray700,
            },
        },
        overrides: {
            MuiButton: {
                root: {
                    borderRadius: '4px',
                },
                contained: {
                    padding: '0.8rem 1.6rem',
                },
                outlined: {
                    padding: '0.8rem 1.6rem',
                },
                containedSizeLarge: {
                    padding: '1.1rem 2.2rem',
                    fontSize: '1.4rem',
                },
                outlinedSizeLarge: {
                    padding: '1.1rem 2.2rem',
                    fontSize: '1.4rem',
                },
                containedPrimary: {
                    border: `1px solid ${colorArray.main}`,
                    color: colorArray.text,
                    '&:hover': {
                        borderColor: colorArray.secondary,
                    },
                    '&:active': {
                        backgroundColor: colorArray.third,
                        borderColor: colorArray.third,
                    },
                    '&:focus': {
                        backgroundColor: colorArray.secondary,
                        borderColor: colorArray.secondary,
                        '&:active': {
                            backgroundColor: colorArray.third,
                            borderColor: colorArray.third,
                        },
                    },
                    '&$disabled': {
                        borderColor: colorPalette.gray700,
                        cursor: 'not-allowed',
                        pointerEvents: 'all',
                    },
                },
                containedSecondary: {
                    border: `1px solid ${colorPalette.gray1000}`,
                    '&:hover': {
                        borderColor: colorPalette.gray900,
                    },
                    '&:active': {
                        backgroundColor: colorPalette.gray800,
                        borderColor: colorPalette.gray800,
                    },
                    '&:focus': {
                        backgroundColor: colorPalette.gray900,
                        borderColor: colorPalette.gray900,
                        '&:active': {
                            backgroundColor: colorPalette.gray800,
                            borderColor: colorPalette.gray800,
                        },
                    },
                    '&$disabled': {
                        borderColor: colorPalette.gray700,
                        cursor: 'not-allowed',
                        pointerEvents: 'all',
                    },
                },
                outlinedPrimary: {
                    color: colorArray.outline || colorArray.main,
                    backgroundColor: colorPalette.white,
                    border: `1px solid ${colorArray.outline || colorArray.main}`,
                    '&:hover': {
                        color: colorArray.text,
                        backgroundColor: colorArray.secondary,
                        borderColor: colorArray.secondary,
                    },
                    '&:active': {
                        color: colorArray.text,
                        backgroundColor: colorArray.third,
                        borderColor: colorArray.third,
                    },
                    '&:focus': {
                        color: colorArray.text,
                        backgroundColor: colorArray.secondary,
                        borderColor: colorArray.secondary,
                        '&:active': {
                            color: colorArray.text,
                            backgroundColor: colorArray.third,
                            borderColor: colorArray.third,
                        },
                    },
                    '&$disabled': {
                        backgroundColor: colorPalette.gray700,
                        borderColor: colorPalette.gray700,
                        cursor: 'not-allowed',
                        pointerEvents: 'all',
                        '&:hover': {
                            color: colorPalette.white,
                            backgroundColor: colorPalette.gray700,
                            borderColor: colorPalette.gray700,
                        },
                    },
                },
                outlinedSecondary: {
                    color: colorPalette.gray1000,
                    backgroundColor: colorPalette.white,
                    border: `1px solid ${colorPalette.gray1000}`,
                    '&:hover': {
                        color: colorPalette.white,
                        backgroundColor: colorPalette.gray900,
                        borderColor: colorPalette.gray900,
                    },
                    '&:active': {
                        color: colorPalette.white,
                        backgroundColor: colorPalette.gray800,
                        borderColor: colorPalette.gray800,
                    },
                    '&:focus': {
                        color: colorPalette.white,
                        backgroundColor: colorPalette.gray900,
                        borderColor: colorPalette.gray900,
                        '&:active': {
                            color: colorPalette.white,
                            backgroundColor: colorPalette.gray800,
                            borderColor: colorPalette.gray800,
                        },
                    },
                    '&$disabled': {
                        backgroundColor: colorPalette.gray700,
                        borderColor: colorPalette.gray700,
                        cursor: 'not-allowed',
                        pointerEvents: 'all',
                        '&:hover': {
                            color: colorPalette.white,
                            backgroundColor: colorPalette.gray700,
                            borderColor: colorPalette.gray700,
                        },
                    },
                },
            },
            MuiCheckbox: {
                colorPrimary: {
                    color: colorPalette.gray500,
                    backgroundColor: colorPalette.white,
                    '&$checked': {
                        color: colorPalette.gray1000,
                        '&:hover': {
                            backgroundColor: 'transparent',
                        },
                    },
                    '&:hover': {
                        color: colorPalette.gray1000,
                        backgroundColor: 'transparent',
                    },
                    '&$disabled': {
                        color: colorPalette.gray500,
                        backgroundColor: colorPalette.gray100,
                    },
                },
            },
            MuiRadio: {
                colorPrimary: {
                    color: colorPalette.gray400,
                    '&$checked': {
                        color: colorPalette.gray1000,
                        '&:hover': {
                            backgroundColor: 'transparent',
                        },
                    },
                    '&:hover': {
                        color: colorPalette.gray1000,
                        backgroundColor: 'transparent',
                    },
                },
            },
            MuiFilledInput: {
                input: {
                    height: 'auto',
                },
                root: {
                    border: `1px solid ${colorPalette.gray300}`,
                    borderRadius: '3px',
                    '@media (max-width:576px)': {
                        fontSize: pxToRem(16),
                    },
                    backgroundColor: colorPalette.white,
                    color: colorPalette.gray800,
                    '&:hover': {
                        backgroundColor: colorPalette.white,
                        borderColor: colorPalette.gray1000,
                    },
                    '&$focused': {
                        backgroundColor: colorPalette.white,
                        borderColor: colorPalette.gray1000,
                    },
                    '&$disabled': {
                        backgroundColor: colorPalette.gray200,
                        '&:hover': {
                            borderColor: colorPalette.gray300,
                        },
                    },
                    '&$error': {
                        borderColor: colorPalette.red600,
                        color: colorPalette.red600,
                    },
                },
            },
            MuiInputLabel: {
                root: {
                    color: colorPalette.gray500,
                    '&$focused': {
                        color: colorPalette.gray500,
                    },
                    '&$error': {
                        color: colorPalette.gray500,
                    },
                },
                filled: {
                    '&$shrink': {
                        '&$marginDense': {
                            transform: 'translate(12px, 7px) scale(0.86)',
                        },
                    },
                },
            },
            MuiSelect: {
                root: {
                    textAlign: 'left',
                },
                select: {
                    '&:focus': {
                        backgroundColor: colorPalette.white,
                    },
                },
                icon: {
                    width: 16,
                    height: 16,
                    top: 'calc(50% - 7px)',
                },
                iconFilled: {
                    right: 12,
                },
                selectMenu: {
                    minHeight: 0,
                },
            },
            MuiFormHelperText: {
                contained: {
                    marginLeft: 1,
                    marginRight: 0,
                },
            },
            MuiAlert: {
                root: {
                    display: 'flex',
                    padding: 8,
                },
                message: {
                    padding: 0,
                    lineHeight: '1.8rem',
                    width: '100%',
                    '& .inline-link-container': {
                        display: 'flex',
                        justifyContent: 'space-between',
                    },
                    '& .bottom-link-container': {
                        marginTop: 8,
                        display: 'flex',
                        justifyContent: 'flex-end',
                    },
                    '& .bottom-link-close-option': {
                        marginRight: -35.33,
                    },
                    '& a': {
                        textDecoration: 'underline',
                    },
                    '& p': {
                        margin: 0,
                    },
                },
                icon: {
                    padding: 0,
                    maxHeight: 18,
                    marginRight: 9.33,
                    '& .MuiSvgIcon-root-22': {
                        height: 13.33,
                        width: 'auto',
                        marginTop: 3,
                    },
                },
                action: {
                    maxHeight: 18,
                    marginRight: 0,
                    '& .MuiSvgIcon-root-22': {
                        height: 13.33,
                        width: 'auto',
                    },
                },
                standardInfo: {
                    color: colorPalette.lightBlue800,
                    backgroundColor: colorPalette.lightBlue100,
                },
                standardSuccess: {
                    color: colorPalette.green800,
                    backgroundColor: colorPalette.green100,
                },
                standardError: {
                    color: colorPalette.red800,
                    backgroundColor: colorPalette.red100,
                    '& .MuiAlert-icon': {
                        color: colorPalette.red800,
                    },
                },
                standardWarning: {
                    color: colorPalette.orange800,
                    backgroundColor: colorPalette.orange100,
                },
            },
            MuiAlertTitle: {
                root: {
                    marginBottom: 2,
                    marginTop: 2,
                },
            },
            MuiSvgIcon: {
                colorPrimary: {
                    color: colorPalette.gray1000,
                },
                colorSecondary: {
                    color: colorPalette.red600,
                },
            },
            MuiIconButton: {
                root: {
                    color: colorPalette.gray800,
                    '&:hover': {
                        backgroundColor: 'transparent',
                    },
                },
            },
            MuiPickersToolbar: {
                toolbar: {
                    backgroundColor: colorPalette.gray1000,
                },
            },
            MuiPickersToolbarText: {
                toolbarBtnSelected: {
                    color: colorPalette.white,
                },
                toolbarTxt: {
                    color: colorPalette.gray500,
                },
            },
            MuiPickersDay: {
                day: {
                    color: colorPalette.gray1000,
                },
                current: {
                    color: colorPalette.gray1000,
                },
                daySelected: {
                    color: colorPalette.white,
                    backgroundColor: colorPalette.gray1000,
                    '&:hover': {
                        backgroundColor: colorPalette.gray1000,
                    },
                },
            },
            MuiPickersCalendarHeader: {
                switchHeader: {
                    '& div p': {
                        color: colorPalette.gray1000,
                    },
                },
                dayLabel: {
                    color: colorPalette.gray500,
                },
            },
        },
        props: {
            MuiButtonBase: {
                disableRipple: true,
            },
            MuiTextField: {
                variant: 'filled',
                margin: 'dense',
                fullWidth: true,
            },
            MuiFilledInput: {
                disableUnderline: true,
            },
            MuiCheckbox: {
                color: 'primary',
            },
            MuiRadio: {
                color: 'primary',
                icon: React.createElement(RadioButtonIcon, { checked: false }),
                checkedIcon: React.createElement(RadioButtonIcon, { checked: true }),
            },
            MuiFormControl: {
                variant: 'filled',
                margin: 'dense',
                fullWidth: true,
            },
            MuiAlert: {
                variant: 'standard',
                action: (React.createElement(IconButton, { color: "inherit", size: "small" },
                    React.createElement(Close, null))),
            },
            MuiSelect: {
                // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
                IconComponent: (props) => React.createElement(ChevronDownOutlined, Object.assign({}, props)),
            },
        },
    };
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return createTheme(theme);
};
